import { useMemo, useState } from 'react'
import { ArrowLeft, Check, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { PO_TEMPLATE_PRESETS, presetMatches, type PoTemplatePreset } from '@/lib/poTemplate/presets'
import { samplePurchaseOrder } from '@/lib/poTemplate/sample'
import { PO_PAPER, PoDocument } from '@/components/inventory/purchase-orders/document/PoDocument'
import { PoScaledPreview } from '@/components/inventory/purchase-orders/document/PoScaledPreview'
import type { PoTemplateLayout } from '@/types/purchaseOrders'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The editor's draft: each preset is built from it, so previews carry the tenant's logo and company. */
  base: PoTemplateLayout
  logoUrl: string | null
  onApply: (preset: PoTemplatePreset) => void
}

/**
 * Configure → Purchase Orders → Browse templates (z8nrz7f7dz): the
 * industry-standard presets, each previewed live as the same PoDocument the
 * editor, Print and PDF draw — built from the tenant's own draft, so the logo
 * and company on every card are theirs. Choosing one only fills the draft.
 */
export function PoTemplateGallery({ open, onOpenChange, base, logoUrl, onApply }: Props) {
  const [previewId, setPreviewId] = useState<string | null>(null)

  // Built only while open: the editor re-renders on every keystroke in the draft.
  const built = useMemo(
    () =>
      open
        ? PO_TEMPLATE_PRESETS.map((preset) => {
            const layout = preset.build(base)
            return { preset, layout, sample: samplePurchaseOrder(layout), current: presetMatches(preset, base) }
          })
        : [],
    [open, base],
  )
  const previewing = built.find((b) => b.preset.id === previewId) ?? null

  function close(next: boolean) {
    if (!next) setPreviewId(null)
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className='flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl'>
        {previewing ? (
          <>
            <DialogHeader className='space-y-1 border-b border-slate-200 px-6 py-4 pr-12'>
              <button
                type='button'
                onClick={() => setPreviewId(null)}
                className='mb-1 inline-flex w-fit items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900'
              >
                <ArrowLeft className='h-3.5 w-3.5' />
                All templates
              </button>
              <DialogTitle>{previewing.preset.name}</DialogTitle>
              <DialogDescription>{previewing.preset.description}</DialogDescription>
            </DialogHeader>
            <div className='min-h-0 flex-1 overflow-auto bg-slate-100 p-6'>
              <div className='mx-auto max-w-[820px]'>
                <PoScaledPreview paperWidthMm={(PO_PAPER[previewing.layout.document.paperSize] ?? PO_PAPER.A4).widthMm}>
                  <PoDocument layout={previewing.layout} po={previewing.sample} logoUrl={logoUrl} mode='preview' />
                </PoScaledPreview>
              </div>
            </div>
            <div className='flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-3'>
              <p className='text-xs text-slate-500'>
                Your logo, company details and ship-to are kept. Nothing is saved until you press Save.
              </p>
              <div className='flex gap-2'>
                <Button variant='outline' size='sm' onClick={() => setPreviewId(null)}>
                  Back
                </Button>
                <Button size='sm' onClick={() => onApply(previewing.preset)} disabled={previewing.current}>
                  {previewing.current ? 'Current template' : 'Use this template'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className='space-y-1 border-b border-slate-200 px-6 py-4 pr-12'>
              <DialogTitle>Choose a purchase order template</DialogTitle>
              <DialogDescription>
                Industry-standard starting points, shown with your logo and company. After choosing one you can still
                edit everything — logo, texts, columns, fields — then Save to make it your template.
              </DialogDescription>
            </DialogHeader>
            <div className='min-h-0 flex-1 overflow-auto bg-slate-50 p-6'>
              <ul className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                {built.map(({ preset, layout, sample, current }) => (
                  <li
                    key={preset.id}
                    className={cn(
                      'flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md',
                      current ? 'border-primary ring-1 ring-primary' : 'border-slate-200',
                    )}
                  >
                    <button
                      type='button'
                      onClick={() => setPreviewId(preset.id)}
                      aria-label={`Preview ${preset.name}`}
                      className='relative block h-72 overflow-hidden border-b border-slate-200 bg-slate-100 px-4 pt-4 text-left'
                    >
                      <div aria-hidden className='pointer-events-none'>
                        <PoScaledPreview paperWidthMm={(PO_PAPER[layout.document.paperSize] ?? PO_PAPER.A4).widthMm}>
                          <PoDocument layout={layout} po={sample} logoUrl={logoUrl} mode='preview' />
                        </PoScaledPreview>
                      </div>
                      <span
                        aria-hidden
                        className='pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-100 via-slate-100/80 to-transparent'
                      />
                      {current && (
                        <span className='absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground shadow'>
                          <Check className='h-3 w-3' />
                          Current
                        </span>
                      )}
                    </button>
                    <div className='flex flex-1 flex-col gap-2 p-4'>
                      <div className='flex items-center gap-2'>
                        <span
                          aria-hidden
                          className='h-2.5 w-2.5 shrink-0 rounded-full'
                          style={{ backgroundColor: layout.branding.accentColor ?? undefined }}
                        />
                        <h3 className='text-sm font-semibold text-slate-900'>{preset.name}</h3>
                      </div>
                      <p className='text-xs leading-relaxed text-slate-600'>{preset.description}</p>
                      <div className='flex flex-wrap gap-1'>
                        {preset.tags.map((t) => (
                          <span key={t} className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className='mt-auto flex gap-2 pt-2'>
                        <Button variant='outline' size='sm' className='flex-1 gap-1.5' onClick={() => setPreviewId(preset.id)}>
                          <Eye className='h-3.5 w-3.5' />
                          Preview
                        </Button>
                        <Button size='sm' className='flex-1' onClick={() => onApply(preset)} disabled={current}>
                          {current ? 'In use' : 'Use this template'}
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
